import React, {useCallback, useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {
  deleteTelegramSubscription,
  fetchTelegramRecipients,
  revokeTelegramInvite,
  updateTelegramSubscription,
} from '@/entities/telegram/api';
import type {
  TelegramPendingInvite,
  TelegramSubscription,
} from '@/entities/telegram/types';
import {shareInvite} from '@/features/manage-telegram-recipients/lib/shareInvite';
import {formatDate} from '@/shared/lib/format';
import {AppButton, IconButton} from '@/shared/ui/button';
import {Card, IconBubble} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Icon} from '@/shared/ui/icons';
import {SectionHeader} from '@/shared/ui/section';
import {SkeletonList} from '@/shared/ui/skeleton';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {Toggle} from '@/shared/ui/toggle';
import {DataList} from '@/shared/ui/data-list';
import {Pager} from '@/shared/ui/pager';
import {useClientPage} from '@/shared/lib/use-client-page';
import {Cell, type Column} from '@/shared/ui/table';
import type {SortAccessors} from '@/shared/lib/table-sort';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Who receives Telegram notifications for this company, and which links are
 * still out there unredeemed. Subscriptions are edited in place — the three
 * toggles are the whole write surface, plus removal. Renders inside a `Screen`
 * the caller already owns (Task 6) — no `Screen` here.
 */
const styles = StyleSheet.create({
  body: {gap: spacing(5), paddingBottom: spacing(8), paddingTop: spacing(2), flexGrow: 1},
  section: {gap: spacing(2)},
  /* Имя чата и его идентификатор — двумя строками в одной ячейке. */
  who: {flex: 1, minWidth: 0, gap: 1},
  nameCell: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), flex: 1, minWidth: 0},
  chips: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1) + 2,
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(2),
    borderRadius: radii.md,
    borderWidth: 1,
  },
  card: {gap: spacing(3)},
  cardHead: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  cardTitle: {flex: 1, gap: 2},
  sub: {gap: spacing(1), paddingTop: spacing(3), borderTopWidth: StyleSheet.hairlineWidth},
  subHead: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  subName: {flex: 1},
  actionRow: {flexDirection: 'row', gap: spacing(2)},
  grow: {flex: 1},
});

/**
 * "kind: name" for department/person scope, or the fixed company label. When
 * `scopeLabel` is '' (sv_control couldn't resolve the target, usually because it
 * was deleted) this falls back to the raw control-native uuid in `scopeId` — same
 * as sv_control's own documented fallback. That is deliberate, not a bug: a stale
 * cached name would misrepresent who the subscription now points at, whereas the
 * id is at least honest about "we no longer know its name".
 */
function scopeText(
  t: ReturnType<typeof useTranslation>['t'],
  scopeType: string,
  scopeLabel: string,
  scopeId: string,
): string {
  if (scopeType === 'company') {
    return t('telegram.scopeCompany');
  }
  const kind =
    scopeType === 'department' ? t('telegram.scopeDepartment') : t('telegram.scopePerson');
  return `${kind}: ${scopeLabel || scopeId}`;
}

/**
 * Same as `scopeText`, for an invite that may carry several targets. Falls back
 * to the id whenever the name could not be resolved (target deleted after the
 * invite was minted) — `scope_labels` is positionally aligned with `scope_ids`,
 * so index N always describes index N.
 */
function inviteScopeText(
  t: ReturnType<typeof useTranslation>['t'],
  invite: TelegramPendingInvite,
): string {
  if (invite.scope_type === 'company') {
    return t('telegram.scopeCompany');
  }
  const kind =
    invite.scope_type === 'department'
      ? t('telegram.scopeDepartment')
      : t('telegram.scopePerson');
  // Older servers answer without `scope_ids`; fall back to the single field so
  // the row still names its target instead of rendering "Сотрудник: ".
  const ids = invite.scope_ids?.length ? invite.scope_ids : [invite.scope_id];
  const labels = invite.scope_labels ?? [];
  const named = ids.map((id, i) => labels[i] || id).filter(Boolean);
  return `${kind}: ${named.join(', ')}`;
}

/**
 * Переключатель-чип: сам себе подпись и сам себе состояние.
 *
 * На карточке телефона нет заголовков столбцов, поэтому подпись нужна, — но тумблер
 * с подписью занимает целую строку, а таких настроек три. Чип говорит то же самое
 * заливкой и галочкой и стоит одну строку на все три.
 */
function FlagChip({
  label,
  on,
  onPress,
}: {
  label: string;
  on: boolean;
  onPress: () => void;
}) {
  const p = useTheme();
  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityState={{checked: on}}
      accessibilityLabel={label}
      onPress={onPress}
      style={state => [
        styles.chip,
        {
          backgroundColor: on
            ? p.accent
            : state.pressed || (state as {hovered?: boolean}).hovered
            ? p.muted
            : p.card,
          borderColor: on ? p.primary : p.border,
        },
      ]}>
      <Icon
        name={on ? 'check' : 'close'}
        size={13}
        color={on ? p.primary : p.mutedForeground}
      />
      <AppText variant="micro" color={on ? p.accentForeground : p.mutedForeground}>
        {label}
      </AppText>
    </Pressable>
  );
}

/** Три флага, которые RPC переписывает разом. */
type SubFlags = {
  notify_arrival: boolean;
  notify_departure: boolean;
  include_in_digest: boolean;
};

/** Строка списка: одна подписка, вместе с чатом, которому она принадлежит. */
type RecipientRow = {
  id: string;
  name: string;
  chatId: string;
  active: boolean;
  scope: string;
  sub: TelegramSubscription;
};

const INITIAL_SORT = {key: 'name', direction: 'asc'} as const;
/** Невыкупленных приглашений на странице. */
const PENDING_PER_PAGE = 10;
const PHONE_SORTS = ['name', 'scope'] as const;
const PHONE_SORT_LABELS: Record<string, string> = {
  name: 'telegram.colRecipient',
  scope: 'telegram.colScope',
};

/** Чем сортируется каждый столбец; переключатели — тоже вопрос («кому шлём приходы»). */
const ACCESSORS: SortAccessors<RecipientRow> = {
  name: row => row.name,
  scope: row => row.scope,
  arrival: row => row.sub.notify_arrival,
  departure: row => row.sub.notify_departure,
  digest: row => row.sub.include_in_digest,
};

function PendingCard({companyId, invite}: {companyId: string; invite: TelegramPendingInvite}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);

  const revoke = useMutation({
    mutationFn: () => revokeTelegramInvite(companyId, invite.code),
    onSettled: () => qc.invalidateQueries({queryKey: ['telegram-recipients', companyId]}),
  });

  return (
    <Card padding={4} style={styles.card}>
      <View style={styles.cardHead}>
        <IconBubble tone={p.warningSoft} size={40}>
          <Icon name="link" size={19} color={p.warning} />
        </IconBubble>
        <View style={styles.cardTitle}>
          <AppText variant="subtitle" numberOfLines={1}>
            {invite.label || t('telegram.newInvite')}
          </AppText>
          <AppText variant="caption" numberOfLines={2}>
            {inviteScopeText(t, invite)}
          </AppText>
          {invite.expires_at_millis > 0 ? (
            <AppText variant="caption">
              {t('telegram.expires', {date: formatDate(invite.expires_at_millis)})}
            </AppText>
          ) : null}
          {invite.multi_use ? (
            <AppText variant="caption">{t('telegram.multiUseActiveHint')}</AppText>
          ) : null}
        </View>
        {invite.multi_use ? (
          <StatusPill tone="pending" size="sm" dot={false} label={t('telegram.multiUse')} />
        ) : null}
      </View>
      <View style={styles.actionRow}>
        <AppButton
          title={t('telegram.share')}
          icon="send"
          size="sm"
          style={styles.grow}
          onPress={() => void shareInvite(t, {deepLink: invite.deep_link, code: invite.code})}
        />
        {confirm ? (
          <AppButton
            title={t('telegram.confirmRevoke')}
            tone="destructive"
            size="sm"
            loading={revoke.isPending}
            onPress={() => revoke.mutate()}
          />
        ) : (
          <AppButton
            title={t('telegram.revoke')}
            tone="ghost"
            size="sm"
            onPress={() => setConfirm(true)}
          />
        )}
      </View>
      {revoke.isError ? (
        <AppText variant="caption" color={p.destructiveInk}>
          {t('telegram.revokeFailed')}
        </AppText>
      ) : null}
    </Card>
  );
}

export function RecipientList({
  companyId,
}: {
  companyId: string;
  /** @deprecated «Добавить» стоит в шапке страницы; список его больше не рисует. */
  onCreate?: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  /** Какую строку спрашиваем «точно отключить?». Одну за раз. */
  const [confirmId, setConfirmId] = useState<string | null>(null);
  /*
    Черновики переключателей — по одному на подписку.

    Живут здесь, а не в строке таблицы: у ячейки нет своего состояния, а три
    переключателя одной строки обязаны видеть общий черновик. Иначе второе нажатие,
    сделанное до ответа сервера, посчитается от устаревшего значения и затрёт
    результат первого — RPC переписывает все три флага разом.
  */
  const [drafts, setDrafts] = useState<Record<string, SubFlags>>({});

  const q = useQuery({
    queryKey: ['telegram-recipients', companyId],
    queryFn: () => fetchTelegramRecipients(companyId),
  });

  // Хук стоит ДО ранних возвратов ниже: порядок хуков не должен зависеть от того,
  // приехали данные или нет.
  const pendingPage = useClientPage(q.data?.pending ?? [], PENDING_PER_PAGE);

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({queryKey: ['telegram-recipients', companyId]});
  }, [qc, companyId]);

  const patch = useMutation({
    mutationFn: ({id, next}: {id: string; next: SubFlags}) =>
      updateTelegramSubscription(companyId, id, next),
    // Неудавшийся PATCH оставляет сервер прежним, поэтому черновик просто снимается:
    // строка вернётся к тому, что действительно лежит на сервере.
    onError: (_e, {id}) =>
      setDrafts(d => {
        const next = {...d};
        delete next[id];
        return next;
      }),
    onSettled: invalidate,
  });

  const drop = useMutation({
    meta: {toast: 'toast.recipientRemoved'},
    mutationFn: (id: string) => deleteTelegramSubscription(companyId, id),
    onSettled: () => {
      setConfirmId(null);
      invalidate();
    },
  });

  if (q.isPending) {
    return (
      <View style={styles.body}>
        <SkeletonList count={3} avatar={false} />
      </View>
    );
  }

  if (q.isError) {
    return (
      <EmptyState
        tone="error"
        title={t('telegram.error')}
        actionLabel={t('actions.retry')}
        onAction={() => void q.refetch()}
      />
    );
  }

  // Получатель без подписок ничего не получает и делать с ним здесь нечего —
  // в списке он был бы вечной строкой без единого действия.
  const all = (q.data?.recipients ?? []).filter(r => r.subscriptions.length > 0);
  const pending = q.data?.pending ?? [];

  /*
    Строка списка — ПОДПИСКА, а не чат.

    Раньше строкой был чат, а подписки лежали внутри него блоками: чат с тремя
    адресатами занимал пол-экрана, и три набора переключателей выглядели как три
    одинаковых, но не связанных настройки. Одна подписка на строку читается как
    таблица прав: кому, про кого, что именно слать.
  */
  const rows: RecipientRow[] = all.flatMap(r =>
    r.subscriptions.map(sub => ({
      id: sub.id,
      name: r.label || r.title || r.chat_id,
      chatId: r.chat_id,
      active: r.is_active,
      scope: scopeText(t, sub.scope_type, sub.scope_label, sub.scope_id),
      sub,
    })),
  );

  const needle = search.trim().toLowerCase();
  const matched = needle
    ? rows.filter(row =>
        [row.name, row.chatId, row.scope]
          .filter(Boolean)
          .some(field => String(field).toLowerCase().includes(needle)),
      )
    : rows;

  /** Что показывает переключатель: черновик, если он есть, иначе ответ сервера. */
  const flagsOf = (row: RecipientRow): SubFlags =>
    drafts[row.id] ?? {
      notify_arrival: row.sub.notify_arrival,
      notify_departure: row.sub.notify_departure,
      include_in_digest: row.sub.include_in_digest,
    };

  const setFlag = (row: RecipientRow, patchOne: Partial<SubFlags>) => {
    const next = {...flagsOf(row), ...patchOne};
    setDrafts(d => ({...d, [row.id]: next}));
    patch.mutate({id: row.id, next});
  };

  /** Один переключатель в ячейке: подпись стоит в заголовке столбца. */
  const flagCell = (row: RecipientRow, key: keyof SubFlags, label: string) => (
    <Toggle
      bare
      label={label}
      value={flagsOf(row)[key]}
      onChange={v => setFlag(row, {[key]: v} as Partial<SubFlags>)}
    />
  );

  const columns: Column<RecipientRow>[] = [
    {
      key: 'name',
      sortKey: 'name',
      title: t('telegram.colRecipient'),
      flex: 2,
      // Отдельного столбца «Состояние» нет: в нём у всех, кроме отключённого чата,
      // стоял бы прочерк — столбец, который девять строк из девяти тратят на тире.
      // Предупреждение стоит там, где на него смотрят, — рядом с именем.
      render: row => (
        <View style={styles.nameCell}>
          <View style={styles.who}>
            <AppText variant="subtitle" numberOfLines={1}>
              {row.name}
            </AppText>
            <AppText variant="mono" numberOfLines={1}>
              {row.chatId}
            </AppText>
          </View>
          {row.active ? null : (
            <StatusPill tone="denied" size="sm" label={t('telegram.inactive')} />
          )}
        </View>
      ),
    },
    {
      key: 'scope',
      sortKey: 'scope',
      title: t('telegram.colScope'),
      flex: 1.6,
      render: row => <Cell tone={p.mutedForeground}>{row.scope}</Cell>,
    },
    {
      key: 'arrival',
      sortKey: 'arrival',
      title: t('telegram.notifyArrival'),
      width: 96,
      render: row => flagCell(row, 'notify_arrival', t('telegram.notifyArrival')),
    },
    {
      key: 'departure',
      sortKey: 'departure',
      title: t('telegram.notifyDeparture'),
      width: 96,
      render: row => flagCell(row, 'notify_departure', t('telegram.notifyDeparture')),
    },
    {
      key: 'digest',
      sortKey: 'digest',
      title: t('telegram.colDigest'),
      width: 110,
      render: row => flagCell(row, 'include_in_digest', t('telegram.includeInDigest')),
    },
    {
      key: 'remove',
      title: '',
      width: 132,
      align: 'right',
      // Подтверждение прямо в ячейке: отписка молча и без спроса — это чат, который
      // перестал получать проходы, и заметят это через неделю.
      render: row =>
        confirmId === row.id ? (
          <AppButton
            title={t('telegram.confirmRemove')}
            tone="destructive"
            size="sm"
            loading={drop.isPending}
            onPress={() => drop.mutate(row.id)}
          />
        ) : (
          <AppButton
            title={t('telegram.remove')}
            tone="ghost"
            size="sm"
            onPress={() => setConfirmId(row.id)}
          />
        ),
    },
  ];

  return (
    <View style={styles.body}>
      <AppText variant="body" color={p.mutedForeground}>
        {t('telegram.intro')}
      </AppText>

      <DataList<RecipientRow>
        testID="telegram-recipients"
        rows={matched}
        total={rows.length}
        columns={columns}
        keyOf={row => row.id}
        accessors={ACCESSORS}
        initialSort={INITIAL_SORT}
        search={{value: search, onChange: setSearch, placeholder: t('telegram.search')}}
        empty={<EmptyState icon="send" title={t('telegram.empty')} />}
        noMatches={<EmptyState icon="send" title={t('telegram.emptyFiltered')} />}
        phoneSorts={PHONE_SORTS}
        sortLabel={key => t(PHONE_SORT_LABELS[key] ?? key)}
        renderCard={row => (
          <Card padding={3} style={styles.card}>
            <View style={styles.cardHead}>
              <IconBubble tone={p.accent} size={32}>
                <Icon name="send" size={15} color={p.primary} />
              </IconBubble>
              <View style={styles.who}>
                <AppText variant="subtitle" numberOfLines={1}>
                  {row.name}
                </AppText>
                <AppText variant="caption" numberOfLines={1}>
                  {row.scope}
                </AppText>
              </View>
              {row.active ? null : (
                <StatusPill tone="denied" size="sm" label={t('telegram.inactive')} />
              )}
              {/* Отписка — иконкой в углу, а не строкой во всю ширину: она нужна
                  редко, а места кнопка занимала как целый переключатель. */}
              <IconButton
                icon={confirmId === row.id ? 'check' : 'trash'}
                label={confirmId === row.id ? t('telegram.confirmRemove') : t('telegram.remove')}
                tone={confirmId === row.id ? 'destructive' : 'ghost'}
                size={32}
                onPress={() =>
                  confirmId === row.id ? drop.mutate(row.id) : setConfirmId(row.id)
                }
              />
            </View>

            {/*
              Три переключателя в РЯД, а не тремя строками.

              Строка «подпись слева, тумблер 50×30 справа» стоит 44pt, и три таких
              превращали карточку одного чата в пол-экрана телефона: девять
              получателей — это шесть экранов прокрутки, чтобы увидеть, кому что
              уходит. Чип и называет настройку, и показывает состояние заливкой, а
              занимает одну строку на все три.
            */}
            <View style={styles.chips}>
              <FlagChip
                label={t('telegram.chipArrival')}
                on={flagsOf(row).notify_arrival}
                onPress={() => setFlag(row, {notify_arrival: !flagsOf(row).notify_arrival})}
              />
              <FlagChip
                label={t('telegram.chipDeparture')}
                on={flagsOf(row).notify_departure}
                onPress={() =>
                  setFlag(row, {notify_departure: !flagsOf(row).notify_departure})
                }
              />
              <FlagChip
                label={t('telegram.chipDigest')}
                on={flagsOf(row).include_in_digest}
                onPress={() =>
                  setFlag(row, {include_in_digest: !flagsOf(row).include_in_digest})
                }
              />
            </View>
          </Card>
        )}
      />

      {patch.isError || drop.isError ? (
        <AppText variant="caption" color={p.destructiveInk}>
          {t('telegram.saveFailed')}
        </AppText>
      ) : null}

      {pending.length > 0 ? (
        <View style={styles.section}>
          <SectionHeader title={t('telegram.pending')} hint={t('telegram.pendingHint')} />
          {/* Невыкупленные ссылки копятся: их раздают пачками, а редактирует их
              никто — поэтому и здесь страницы, а не всё разом. */}
          {pendingPage.rows.map(i => (
            <PendingCard key={i.code} companyId={companyId} invite={i} />
          ))}
          {pending.length > PENDING_PER_PAGE ? (
            <Pager
              page={pendingPage.page}
              totalPages={pendingPage.totalPages}
              total={pendingPage.total}
              perPage={pendingPage.perPage}
              onPage={pendingPage.setPage}
              testID="telegram-pending-pager"
            />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
